import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Chip, 
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Alert,
  Snackbar,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { formGradimentoService } from '../../services/FormGradimento/FormGradimentoService';

const ProjectsEvaluationList = () => {
  const [projectsData, setProjectsData] = useState({
    listaValutazioniDaDare: [],
    listaValutazioniDate: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const risposta = await formGradimentoService.getListaValutazioni();
        const data = risposta || {};
        setProjectsData({
          listaValutazioniDaDare: data.listaValutazioniDaDare || [],
          listaValutazioniDate: data.listaValutazioniDate || []
        });

        setLoading(false);
        
      } catch (err) {
        setError('Si è verificato un errore durante il caricamento dei progetti');
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleClickValutazione = (project, isValutato) => {
    if (isValutato) {
      // Naviga alla pagina di riepilogo valutazione per progetti già valutati
      navigate(`/riepilogoValutazioneIntermedia`); // navigate(`/riepilogoValutazioneIntermedia/${project.idSottoprogetto}`);
    } else {
      // Naviga al form di valutazione per progetti da valutare
      navigate(`/formGradimento`); // navigate(`/formGradimento/${project.idSottoprogetto}`)
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderizzaListeValutazioni = (isValutato) => {
    const projectsList = isValutato 
      ? projectsData.listaValutazioniDate 
      : projectsData.listaValutazioniDaDare;

    if (!projectsList || projectsList.length === 0) {
      return (
        <Box sx={{ 
          py: 4, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          color: 'text.secondary'
        }}>
          <Typography variant="body1" color="text.secondary">
            {isValutato 
              ? 'Non hai ancora valutato nessun progetto' 
              : 'Non ci sono progetti da valutare al momento'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        {projectsList.map((valutazione, index) => (
          <Card 
            key={index} 
            sx={{ 
              mb: 2,
              borderLeft: isValutato ? '4px solid #4caf50' : '4px solid #2196f3',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#ededed'
              }
            }}
          >
            <CardActionArea onClick={() => handleClickValutazione(valutazione, isValutato)}>
              <CardContent>
                 <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <Box>
                    <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                      {isValutato ? (
                        <AssignmentTurnedInIcon sx={{ color: '#26b526', fontSize: 35, marginRight: '10px' }} />
                      ) : (
                        <AssignmentIcon color="primary" sx={{ fontSize: 35, marginRight: '10px' }} />
                      )}
                      {valutazione.titolo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {valutazione.descrizione}
                    </Typography>
                    {/*{valutazione.puoDareValutazione === false && (
                      <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                        {valutazione.motivazioneErrore || "Ancora non è possibile valutare questo progetto"}
                      </Alert>
                    )}
                    {isValutato && valutazione.dataValutazione && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Valutato il: {formatDate(valutazione.dataValutazione)}
                      </Typography>
                    )}*/}
                  </Box>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh' 
        }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ 
          position: 'relative',
          display: 'inline-block',
          mb: 3,
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            bottom: -8,
            width: 40,
            height: 3,
            bgcolor: 'primary.main'
          }
        }}
      >
        Valutazione Progetti
      </Typography>
      {/*<Typography 
        variant="body1" 
        color="text.secondary" 
        paragraph
        sx={{ maxWidth: 600, mb: 4 }}
      >
        Visualizza i progetti disponibili per la valutazione e quelli che hai già valutato.
      </Typography>*/}

      <Grid container spacing={4}>
        {/* Colonna progetti da valutare */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon color="primary" sx={{ mr: 1 }} />
            Progetti da Valutare
          </Typography>
          {renderizzaListeValutazioni(false)}
        </Grid>

        {/* Colonna progetti già valutati */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center' }}>
            <AssignmentTurnedInIcon sx={{ color: '#26b526', mr: 1 }} />
            Progetti Già Valutati
          </Typography>
          {renderizzaListeValutazioni(true)}
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProjectsEvaluationList;